import jexl from 'jexl';
import { getValueByPath } from './json-path-parser';

// Configure jexl with custom transforms for common use cases
jexl.addTransform('upper', (val: string) => val?.toUpperCase() || '');
jexl.addTransform('lower', (val: string) => val?.toLowerCase() || '');
jexl.addTransform('trim', (val: string) => val?.trim() || '');
jexl.addTransform('length', (val: string | any[]) => val?.length || 0);
jexl.addTransform('default', (val: any, defaultValue: any) => val ?? defaultValue);

// Token pattern to match {{field}} or {{node.field}} syntax
const TOKEN_PATTERN = /\{\{([^}]+)\}\}/g;

/**
 * Evaluates an expression string with context data
 * Supports both {{token}} syntax and jexl expressions
 */
export function evaluateExpression(expression: string, context: Record<string, any>): any {
  if (!expression) return '';
  
  try {
    // First, replace {{token}} syntax with direct context access
    const processedExpression = expression.replace(TOKEN_PATTERN, (match, tokenPath) => {
      const path = tokenPath.trim();
      
      // Use enhanced JSON path parser that supports array indexing
      const value = getValueByPath(context, path);
      
      // Return the raw value for jexl to process
      return JSON.stringify(value);
    });
    
    // Check if it's a simple string concatenation (no jexl operators)
    if (!processedExpression.includes('|') && !processedExpression.includes('+') && 
        !processedExpression.includes('?') && !processedExpression.includes('=')) {
      // Simple token replacement
      if (processedExpression.startsWith('"') && processedExpression.endsWith('"')) {
        return JSON.parse(processedExpression);
      }
      return processedExpression;
    }
    
    // Use jexl for complex expressions
    return jexl.evalSync(processedExpression, context);
  } catch (error) {
    console.warn('Expression evaluation error:', error);
    return expression; // Return original expression if evaluation fails
  }
}

/**
 * Validates an expression string for syntax errors
 */
export function validateExpression(expression: string): { valid: boolean; error?: string } {
  if (!expression) return { valid: true };
  
  try {
    // Check for unmatched brackets
    const openBrackets = (expression.match(/\{\{/g) || []).length;
    const closeBrackets = (expression.match(/\}\}/g) || []).length;
    
    if (openBrackets !== closeBrackets) {
      return { valid: false, error: 'Unmatched brackets in expression' };
    }
    
    // Try to compile the expression (with dummy context)
    const testExpression = expression.replace(TOKEN_PATTERN, '"test"');
    jexl.compile(testExpression);
    
    return { valid: true };
  } catch (error) {
    return { valid: false, error: error instanceof Error ? error.message : 'Invalid expression' };
  }
}

/**
 * Evaluates all mappings in a transform configuration
 */
export function evaluateTransformMappings(
  mappings: Record<string, string>, 
  context: Record<string, any>
): Record<string, any> {
  const result: Record<string, any> = {};
  
  Object.entries(mappings).forEach(([field, expression]) => {
    result[field] = evaluateExpression(expression, context);
  });
  
  return result;
}

/**
 * Extracts tokens from an expression string
 */
export function extractTokens(expression: string): string[] {
  const tokens: string[] = [];
  let match;
  
  while ((match = TOKEN_PATTERN.exec(expression)) !== null) {
    tokens.push(match[1].trim());
  }
  
  return tokens;
}

/**
 * Gets available tokens from upstream context
 */
export function getAvailableTokens(context: Record<string, any>): string[] {
  const tokens: string[] = [];
  
  function extractKeysRecursive(obj: any, prefix = ''): void {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      Object.keys(obj).forEach(key => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        tokens.push(fullKey);
        
        // Recursively extract nested keys (but limit depth to avoid infinite loops)
        if (prefix.split('.').length < 3) {
          extractKeysRecursive(obj[key], fullKey);
        }
      });
    }
  }
  
  extractKeysRecursive(context);
  return tokens;
} 